import { RoutesManager } from 'utils/routing/routesManager'
import { NavigationBreadCrumbs } from 'components/appLayout/appNavigation/breadcrumbs/NavigationBreadCrumbs'
import React, { useMemo } from 'react'
import { ContentGroup, DataGrid, EmptyState, Spinner } from '@ketch-com/deck'
import { ReactComponent as EmptyStateIcon } from 'assets/icons/emptyState/EmptyStateCommon.svg'
import { useProcessingActivitiesPaginated } from 'api/processingActivities/queries/useProcessingActivitiesPaginated'
import { Box } from '@mui/material'
import { purposesProcessingActivitiesColumns } from './utils/purposesTabProcessingActivitiesUtils'
import { usePurposeViewContext } from 'pages/policyCenter/purposes/view/context/PurposeViewContext'

export const PurposesTabProcessingActivities: React.FC = () => {
  const { purpose } = usePurposeViewContext()
  const {
    data: processingActivities,
    pagination,
    isLoading,
  } = useProcessingActivitiesPaginated({
    params: {
      purposeCodes: [purpose?.code || ''],
    },
  })

  const totalPages = pagination.totalPages
  const rowsPerPage = useMemo(
    () => (pagination.totalResults ? Math.ceil(pagination.totalResults / totalPages) : 20),
    [pagination.totalResults, totalPages],
  )
  const paginationModel = useMemo(
    () => ({
      page: pagination.page,
      pageSize: rowsPerPage,
    }),
    [pagination.page, rowsPerPage],
  )

  const onPaginationModelChange = (paginationModel: any) => {
    pagination.onPageChange(paginationModel.page)
  }

  return (
    <>
      <NavigationBreadCrumbs
        type="dark"
        items={[
          { title: 'Privacy Program', link: RoutesManager.policyCenter.root.getURL() },
          { title: 'Purposes', link: RoutesManager.policyCenter.purposes.root.getURL() },
          {
            title: purpose?.name || purpose?.code,
            link: RoutesManager.policyCenter.purposes.view.root.getURL({ code: purpose?.code || '' }),
          },
          { title: 'Processing Activities' },
        ]}
      />

      {isLoading ? (
        <Box display="flex" margin="100px" justifyContent="center" alignItems="center">
          <Spinner size="32px" thickness={2.5} />
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={4}>
          <ContentGroup
            title="Processing Activities"
            titleVariant="h3"
            variant="inner-page-header"
            subTitle="All processing activities associated with this Purpose."
          />

          {/* Table of processing activities */}
          {processingActivities.length ? (
            <DataGrid
              sx={{
                padding: 'unset',
                height: 'auto',
              }}
              autosizeOnMount
              autosizeOptions={{
                includeHeaders: true,
                includeOutliers: false,
                columns: purposesProcessingActivitiesColumns.map(column => column.field),
                expand: true,
              }}
              columns={purposesProcessingActivitiesColumns}
              disableChildrenSorting
              disableColumnMenu
              disableColumnPinning
              disableColumnReorder
              disableColumnResize
              disableRowHoverStates
              disableRowSelectionOnClick
              getRowId={row => row.id}
              rows={processingActivities}
              getRowHeight={() => 'auto'}
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={onPaginationModelChange}
              pageSizeOptions={[rowsPerPage]}
              rowCount={pagination.totalResults}
              hideFooter={totalPages <= 1}
            />
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              padding={6}
              borderRadius="11px"
              bgcolor="bone.main"
            >
              <EmptyState title="No processing activities mapped to this purpose" customIcon={<EmptyStateIcon />} />
            </Box>
          )}
        </Box>
      )}
    </>
  )
}
